import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  usePutPlayerIdName,
  usePutPlayerIdReassign,
  getGetPlayerIdViewQueryKey,
} from '@/api/generated/player-player/player-player'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectSeparator,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { noAutofillProps, noAutofillClass } from '@/lib/noAutofill'
import type { PlayerCardData } from './types'

/**
 * Commissioner/admin-only player actions. The whole tab is gated on
 * `canManage`; each action is gated on its own server flag (`canRename` /
 * `canReassign`) so the card never has to make a permission decision itself.
 * On success we invalidate the player view query so the card header (name /
 * team) reflects the change.
 */
export function ManageTab({ player }: { player: PlayerCardData }) {
  const qc = useQueryClient()
  const refreshView = () =>
    qc.invalidateQueries({ queryKey: getGetPlayerIdViewQueryKey(player.id) })

  // --- Rename ---
  const [firstName, setFirstName] = useState(player.firstName)
  const [lastName, setLastName] = useState(player.lastName)
  const rename = usePutPlayerIdName({ mutation: { onSuccess: refreshView } })
  const nameValid = firstName.trim().length > 0 && lastName.trim().length > 0
  const nameChanged =
    firstName.trim() !== player.firstName || lastName.trim() !== player.lastName

  // --- Reassign --- (team list is server-provided on the view; no extra fetch)
  const teams = player.reassignTeams ?? []
  const currentTeamId = player.team?.id != null ? String(player.team.id) : ''
  // Free agency is teamId 0 on the API. Offer it only for a rostered player —
  // releasing an already-free agent to free agency is a no-op.
  const FREE_AGENCY = '0'
  const showFreeAgency = player.team != null
  const [teamId, setTeamId] = useState(currentTeamId)
  const [confirming, setConfirming] = useState(false)
  const reassign = usePutPlayerIdReassign({
    mutation: { onSuccess: () => { refreshView(); setConfirming(false) } },
  })
  const teamChanged = teamId !== '' && teamId !== currentTeamId
  const toFreeAgency = teamId === FREE_AGENCY
  const targetTeam = teams.find((t) => String(t.id) === teamId)
  const targetTeamLabel = targetTeam ? `${targetTeam.city} ${targetTeam.name}`.trim() : ''

  return (
    <div className="space-y-6 py-3">
      {player.canRename && (
        <section>
          <h3 className="text-xs font-bold uppercase tracking-wide text-navy">Rename player</h3>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
              First name
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={cn('h-9 w-40', noAutofillClass)}
                {...noAutofillProps}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
              Last name
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={cn('h-9 w-40', noAutofillClass)}
                {...noAutofillProps}
              />
            </label>
            <Button
              type="button"
              disabled={!nameChanged || !nameValid || rename.isPending}
              onClick={() =>
                rename.mutate({
                  id: player.id,
                  data: { firstName: firstName.trim(), lastName: lastName.trim() },
                })
              }
            >
              {rename.isPending ? 'Renaming…' : 'Rename'}
            </Button>
          </div>
          {rename.isSuccess && <p className="mt-1.5 text-xs font-medium text-green-600">Name updated.</p>}
          {rename.isError && <p className="mt-1.5 text-xs font-medium text-red-600">Couldn&apos;t update the name. Try again.</p>}
        </section>
      )}

      {player.canReassign && (
        <section>
          <h3 className="text-xs font-bold uppercase tracking-wide text-navy">Reassign player</h3>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <Select
              value={teamId}
              onValueChange={(v) => { setTeamId(v); setConfirming(false) }}
            >
              <SelectTrigger className="h-9 w-56">
                <SelectValue placeholder="Choose a destination" />
              </SelectTrigger>
              <SelectContent>
                {teams.map((t) => (
                  <SelectItem key={t.id} value={String(t.id)}>
                    {`${t.city} ${t.name}`.trim()}
                  </SelectItem>
                ))}
                {showFreeAgency && (
                  <>
                    <SelectSeparator />
                    <SelectItem value={FREE_AGENCY} className="text-red-600 focus:text-red-600">
                      Free Agency
                    </SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
            {!confirming && (
              <Button
                type="button"
                disabled={!teamChanged}
                onClick={() => setConfirming(true)}
              >
                Reassign
              </Button>
            )}
          </div>
          {/* Two-step confirm — reassign is a bigger action than a rename. */}
          {confirming && (targetTeam || toFreeAgency) && (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-slate-700">
              <span>
                {toFreeAgency ? (
                  <>Release <strong>{player.fullName}</strong> to free agency?</>
                ) : (
                  <>Move <strong>{player.fullName}</strong> to <strong>{targetTeamLabel}</strong>?</>
                )}
              </span>
              <Button
                type="button"
                size="sm"
                disabled={reassign.isPending}
                onClick={() => reassign.mutate({ id: player.id, data: { teamId: Number(teamId) } })}
              >
                {reassign.isPending ? 'Reassigning…' : 'Confirm'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={reassign.isPending}
                onClick={() => setConfirming(false)}
              >
                Cancel
              </Button>
            </div>
          )}
          {reassign.isSuccess && <p className="mt-1.5 text-xs font-medium text-green-600">Player reassigned.</p>}
          {reassign.isError && <p className="mt-1.5 text-xs font-medium text-red-600">Couldn&apos;t reassign. Try again.</p>}
        </section>
      )}
    </div>
  )
}
